import { Stack, Text, Paper, Badge, Group, Divider, Tooltip } from '@mantine/core';
import { IconBook, IconClock } from '@tabler/icons-react';
import DataTables from 'components/DataTables';
import React from 'react';

interface Facilitator {
  name: string;
  email: string;
  type: string;
  binusianID: string;
  approvalLevel: string | null;
  linkURL: string;
}

interface Course {
  trLCMSPlanningCoursesID: string;
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  deliveryType: string;
  learningHours: number;
  modeOfDelivery: string;
  facilitators?: Facilitator[];
}

interface PlanCoursesSectionProps {
  courses: Course[];
  totalLearningHours: number;
}

const PlanCoursesSection: React.FC<PlanCoursesSectionProps> = ({ courses, totalLearningHours }) => {
  const columns = [
    {
      heading: 'Course Information',
      props: (row: Course) => (
        <div>
          <Text fw={500} size='sm' lineClamp={1}>
            {row.courseName}
          </Text>
          {row.courseCode && (
            <Text size='xs' c='dimmed'>
              {row.courseCode}
            </Text>
          )}
        </div>
      ),
      active: true,
      id: 'courseName'
    },
    {
      heading: 'Facilitators',
      props: (row: Course) => (
        <>
          {row.facilitators && row.facilitators.length > 0 ? (
            <Stack gap={6}>
              {row.facilitators.map((facilitator, index) => (
                <Group key={index} gap={8} wrap='nowrap'>
                  <Stack gap={0} style={{ flex: 1 }}>
                    <Group gap={6}>
                      <Text size='xs' fw={500}>
                        {facilitator.name}
                      </Text>
                      <Tooltip label={facilitator.type} withArrow>
                        <Badge
                          size='xs'
                          variant='light'
                          color={facilitator.type === 'PIC Approval' ? 'violet' : 'teal'}
                          style={{ cursor: 'help' }}
                        >
                          {facilitator.type === 'PIC Approval' ? 'PIC' : 'SME'}
                        </Badge>
                      </Tooltip>
                    </Group>
                    <Text size='xs' c='dimmed'>
                      {facilitator.email}
                    </Text>
                  </Stack>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text size='xs' c='dimmed'>
              No facilitator assigned
            </Text>
          )}
        </>
      ),
      active: true,
      id: 'facilitators'
    },
    {
      heading: 'Delivery Type',
      props: (row: Course) => (
        <Badge variant='light' color='blue' size='sm'>
          {row.modeOfDelivery}
        </Badge>
      ),
      active: true,
      id: 'modeOfDelivery'
    },
    {
      heading: 'Learning Hours',
      props: (row: Course) => (
        <Group gap={4}>
          <IconClock size={14} />
          <Text size='sm'>{row.learningHours}h</Text>
        </Group>
      ),
      active: true,
      id: 'learningHours'
    }
  ];

  return (
    <Paper withBorder p={20} radius='lg'>
      <Stack gap={16}>
        <Group justify='space-between' align='center'>
          <Group gap={8}>
            <IconBook size={20} />
            <Text fw={600} size='lg'>
              Planned Courses ({courses.length})
            </Text>
          </Group>
          <Badge variant='outline' size='lg'>
            Total: {totalLearningHours}h
          </Badge>
        </Group>

        <Divider />

        {courses.length > 0 ? (
          <DataTables
            columns={columns}
            data={courses}
            loading={false}
            isNumbering={true}
            withPagination={false}
            emptyMessageTitle='No courses assigned'
            emptyMessageDescription='No courses have been assigned to this plan yet'
          />
        ) : (
          <Text c='dimmed' ta='center' py={40}>
            No courses assigned to this plan
          </Text>
        )}
      </Stack>
    </Paper>
  );
};

export default PlanCoursesSection;
